// External Imports
import {
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { useEffect, useState } from "react";
import { BiSolidChevronLeft, BiSolidChevronRight } from "react-icons/bi";
import { stringToDate } from "../../util/format-date";
import { MdCheck, MdClose } from "react-icons/md";

/** ========================================================================
 * * DateFilter Component
 * This component renders a form to flter a list by start and end date.
 * ========================================================================== */

export default function DateFilter({
  onSubmit,
  startDate: sd,
  endDate: ed,
  defaultPeriod,
}) {
  const [period, setPeriod] = useState(defaultPeriod || "All");

  // =================== SELECT CUSTOM RANGE ===================
  const selectDateRange = (e) => {
    e.preventDefault();
    // Get start and end date text input
    const start = e.target.start.value;
    const end = e.target.end.value;

    // Validate date format
    const regex = /^\d{4}[-/](0[1-9]|1[0-2])[-/](0[1-9]|[12][0-9]|3[01])$/;
    if (!regex.test(start) || !regex.test(end)) return;

    // Convert text to date
    let startDate = stringToDate(start, "00:00");
    let endDate = stringToDate(end, "23:59");

    // If end date is before start date, start date to end date sod
    if (endDate < startDate) startDate = stringToDate(end, "00:00");

    // Submit the date range
    onSubmit(startDate.toISOString(), endDate.toISOString());
    setPeriod("Custom");
  };

  // =================== MOVE PERIOD HANDLER ===================
  const movePeriod = (next) => {
    if (!sd || !ed) return;
    const newStartDate = new Date(sd);
    const newEndDate = new Date(ed);

    if (period === "Today") {
      newStartDate.setDate(newStartDate.getDate() + (next ? 1 : -1));
      onSubmit(
        startOfDay(newStartDate).toISOString(),
        endOfDay(newStartDate).toISOString()
      );
    }
    if (period === "Week") {
      newStartDate.setDate(newStartDate.getDate() + (next ? 7 : -7));
      onSubmit(
        startOfWeek(newStartDate).toISOString(),
        endOfWeek(newStartDate).toISOString()
      );
    }
    if (period === "Month") {
      newStartDate.setMonth(newStartDate.getMonth() + (next ? 1 : -1));
      onSubmit(
        startOfMonth(newStartDate).toISOString(),
        endOfMonth(newStartDate).toISOString()
      );
    }
    if (period === "Year") {
      newStartDate.setFullYear(newStartDate.getFullYear() + (next ? 1 : -1));
      onSubmit(
        startOfYear(newStartDate).toISOString(),
        endOfYear(newStartDate).toISOString()
      );
    }
    if (period === "Custom") {
      const numberOfDays = differenceInDays(new Date(ed), new Date(sd));
      newStartDate.setDate(
        newStartDate.getDate() + (next ? numberOfDays : -numberOfDays)
      );
      newEndDate.setDate(
        newEndDate.getDate() + (next ? numberOfDays : -numberOfDays)
      );
      onSubmit(newStartDate.toISOString(), newEndDate.toISOString());
    }
  };

  // =================== CHANGE PERIOD =================== //
  const changePeriod = (newPeriod) => {
    const today = new Date();
    if (newPeriod === "Week")
      onSubmit(
        startOfWeek(today).toISOString(),
        endOfWeek(today).toISOString()
      );
    if (newPeriod === "Month")
      onSubmit(
        startOfMonth(today).toISOString(),
        endOfMonth(today).toISOString()
      );
    if (newPeriod === "Year")
      onSubmit(
        startOfYear(today).toISOString(),
        endOfYear(today).toISOString()
      );
    if (newPeriod === "Today")
      onSubmit(startOfDay(today).toISOString(), endOfDay(today).toISOString());
    if (newPeriod === "All") onSubmit();

    setPeriod(newPeriod);
  };

  /* =================== CHANGE DATE =================== 
  This handles changing displayed dates
  */
  useEffect(() => {
    if (sd && ed) {
      document.getElementsByName("start")[0].value = format(
        new Date(sd),
        "yyyy-MM-dd"
      );
      document.getElementsByName("end")[0].value = format(
        new Date(ed),
        "yyyy-MM-dd"
      );
    } else {
      document.getElementsByName("start")[0].value = "";
      document.getElementsByName("end")[0].value = "";
      setPeriod("All");
    }
  }, [sd, ed]);

  return (
    <form onSubmit={selectDateRange} className="items-center hidden space-x-4 lg:flex">
      <BiSolidChevronLeft onClick={() => movePeriod(false)} className="icon" />
      <input
        type="date"
        className="text-sm outline-none cursor-pointer hover:text-stone-400 bg-stone-900"
        name="start"
        onClick={() => setPeriod("Custom")}
      />
      {period !== "Custom" ? (
        <select
          onChange={(e) => changePeriod(e.target.value)}
          value={period}
          className="flex text-sm cursor-pointer input hover:text-stone-400"
        >
          <option value="All">All</option>
          <option value="Today">Today</option>
          <option value="Week">Week</option>
          <option value="Month">Month</option>
          <option value="Year">Year</option>
          <option value="Custom">Custom</option>
        </select>
      ) : (
        <div className="flex items-center my-1 space-x-6">
          <MdClose
            onClick={() => changePeriod(defaultPeriod || "All")}
            className="text-2xl icon"
          />
          <button type="submit" className="cursor-pointer hover:text-stone-400">
            <MdCheck className="text-2xl icon" />
          </button>
        </div>
      )}
      <input
        type="date"
        name="end"
        className="text-sm outline-none cursor-pointer hover:text-stone-400 bg-none bg-stone-900"
        onClick={() => setPeriod("Custom")}
      />
      <BiSolidChevronRight onClick={() => movePeriod(true)} className="icon" />
    </form>
  );
}
